/**
 * Attachment service - handles attachment operations for multiple entity types (currently images only)
 * Implements Single Responsibility Principle (SRP) by isolating attachment logic
 *
 * Supported entity types: note, recon_request, quote
 * Future: Will support documents, videos, and other file types
 */

import axios from "axios";
import apiService from "./apiService";
import { RECON } from "./apiConstant";

const resolveFileType = (mimeType = "") => {
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  return "document";
};

/**
 * Delete an attachment
 * @param {string} photoId - Attachment ID to delete
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteAttachment = async (photoId) => {
  try {
    await apiService.delete(RECON.attachments, {
      data: { photo_ids: [photoId] }
    });
    return { success: true };
  } catch (error) {
    console.error("Failed to delete attachment:", error);
    return {
      success: false,
      error: error.response?.data?.detail || error.message || "Delete failed",
    };
  }
};

/**
 * Delete multiple attachments
 * @param {Array<string>} photoIds - Array of attachment IDs to delete
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteMultipleAttachments = async (photoIds) => {
  try {
    await apiService.delete(RECON.attachments, {
      data: { photo_ids: photoIds }
    });
    return { success: true };
  } catch (error) {
    console.error("Failed to delete attachments:", error);
    return {
      success: false,
      error: error.response?.data?.detail || error.message || "Delete failed",
    };
  }
};

/**
 * Upload-once: presign a one-time PUT URL (no DB row) and upload the bytes to R2.
 * Returns the object_key to reuse for the AI call and for attachObject().
 * @param {File} file - already-resized file to upload
 * @returns {Promise<{success: boolean, object_key?: string, error?: string}>}
 */
export const presignUpload = async (file) => {
  try {
    const { object_key, upload_url } = await apiService.post(RECON.presign, {
      file_name: file.name,
      mime_type: file.type || "image/jpeg",
    });
    // Bytes go straight to R2 once; bare axios bypasses the JWT interceptor.
    await axios.put(upload_url, file, {
      headers: { "Content-Type": file.type || "image/jpeg" },
    });
    return { success: true, object_key };
  } catch (error) {
    console.error("presignUpload failed:", error);
    return {
      success: false,
      error: error.response?.data?.detail || error.message || "Upload failed",
    };
  }
};

/**
 * Register an already-uploaded R2 object (from presignUpload) as an attachment on
 * an entity — no re-upload. The backend generates variants in a bounded background
 * task, or reuses existing variants if the object was already processed.
 * @param {{objectKey: string, entityType: string, entityId: string, file: File, userEmail: string}} args
 * @returns {Promise<{success: boolean, photo_id?: string, error?: string}>}
 */
export const attachObject = async ({ objectKey, entityType, entityId, file, userEmail }) => {
  try {
    const result = await apiService.post(RECON.attach, {
      object_key: objectKey,
      entity_type: entityType,
      entity_id: entityId,
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type || "image/jpeg",
      file_type: resolveFileType(file.type),
      user_id: userEmail,
    });
    return { success: true, photo_id: result.photo_id };
  } catch (error) {
    console.error("attachObject failed:", error);
    return {
      success: false,
      error: error.response?.data?.detail || error.message || "Attach failed",
    };
  }
};

/**
 * Create the async "AI summary" note for the Create-Request flow and attach the
 * already-uploaded images to it (by object key — no re-upload). Backend-owned, so
 * it survives the modal closing. Returns the created note (incl. note_id).
 * @param {{vehicleInventoryId: string, summaryText: string, images: Array<{object_key:string,file_name:string,file_size:number,mime_type:string,file_type?:string}>, userEmail: string}} args
 * @returns {Promise<{success: boolean, note?: {note_id: string}, attached_images?: number}>}
 */
export const createAiSummaryNote = async ({ vehicleInventoryId, summaryText, images, userEmail }) => {
  return apiService.post(RECON.vehicleNoteAiSummary, {
    vehicle_inventory_id: vehicleInventoryId,
    user_email: userEmail,
    summary_text: summaryText,
    images: (images || []).map((img) => ({
      object_key: img.object_key,
      file_name: img.file_name,
      file_size: img.file_size,
      mime_type: img.mime_type,
      file_type: img.file_type || "image",
    })),
  });
};

/**
 * Upload a single attachment for any entity type
 * @param {File} file - File object to upload
 * @param {string} entityType - Entity type ('note', 'recon_request', 'quote')
 * @param {string} entityId - Entity ID (note_id for notes, request_id for recon_request, quote_id for quotes)
 * @param {string} userEmail - User email for tracking
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<{success: boolean, photo_id?: string, error?: string}>}
 */
export const uploadEntityAttachment = async (
  file,
  entityType,
  entityId,
  userEmail,
  onProgress
) => {
  try {
    const fileType = resolveFileType(file.type);
    console.log(`[uploadEntityAttachment] Starting upload for ${file.name} to ${entityType}/${entityId}`);

    // Step 1: Get presigned upload URL from backend (creates 'pending' DB record)
    const uploadData = await apiService.post(RECON.uploadUrl, {
      entity_type: entityType,
      entity_id: entityId,
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type,
      file_type: fileType,
      user_id: userEmail,
    });

    console.log(`[uploadEntityAttachment] Got presigned URL for ${file.name}, photo_id: ${uploadData.photo_id}`);

    // Step 2: Upload file directly to R2 using presigned URL
    await axios.put(uploadData.upload_url, file, {
      headers: {
        "Content-Type": file.type,
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });

    console.log(`[uploadEntityAttachment] File uploaded to R2 for ${file.name}`);

    // Step 3: Confirm upload completion with backend (marks as 'completed')
    try {
      await apiService.post(RECON.confirmUpload, {
        photo_id: uploadData.photo_id,
      });
      console.log(`[uploadEntityAttachment] Upload confirmed for ${file.name}`);
    } catch (confirmError) {
      console.error(
        `[uploadEntityAttachment] Confirmation failed for ${file.name}:`,
        confirmError
      );
      // Note: If confirmation fails, the record will remain as 'pending'
      // and will be cleaned up after 2 hours by the scheduled cleanup job
      throw confirmError; // Re-throw so we know upload wasn't fully successful
    }

    return {
      success: true,
      photo_id: uploadData.photo_id,
    };
  } catch (error) {
    console.error(`[uploadEntityAttachment] Upload failed for ${file.name}:`, error);
    return {
      success: false,
      error: error.response?.data?.detail || error.message || "Upload failed",
    };
  }
};

/**
 * Upload multiple attachments for any entity type
 * @param {Array<File>} files - Array of files to upload
 * @param {string} entityType - Entity type ('note', 'recon_request', 'quote')
 * @param {string} entityId - Entity ID
 * @param {string} userEmail - User email
 * @param {Function} onFileProgress - Callback (fileIndex, progress)
 * @returns {Promise<Array<{success: boolean, file: File, photo_id?: string, error?: string}>>}
 */
export const uploadMultipleEntityAttachments = async (
  files,
  entityType,
  entityId,
  userEmail,
  onFileProgress
) => {
  const results = [];

  // Upload files sequentially to avoid overwhelming the server
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const result = await uploadEntityAttachment(
      file,
      entityType,
      entityId,
      userEmail,
      (progress) => {
        if (onFileProgress) {
          onFileProgress(i, progress);
        }
      }
    );

    results.push({
      ...result,
      file,
    });
  }

  return results;
};

/**
 * Fetch all attachments for any entity type with download URLs
 * IMPORTANT: Only returns successfully completed uploads (not pending ones)
 * @param {string} entityType - Entity type ('note', 'recon_request', 'quote')
 * @param {string} entityId - Entity ID
 * @returns {Promise<Array<Object>>} Array of attachment objects with download URLs
 */
export const fetchEntityAttachments = async (entityType, entityId) => {
  try {
    console.log(`[fetchEntityAttachments] Fetching attachments for ${entityType}/${entityId}`);

    // Fetch attachment records from backend
    // Backend automatically filters to only return 'completed' uploads
    const attachments = await apiService.get(
      `${RECON.attachments}?entity_type=${entityType}&entity_id=${entityId}`
    );

    console.log(`[fetchEntityAttachments] Found ${attachments?.length || 0} attachments for ${entityType}/${entityId}`);

    if (!attachments || attachments.length === 0) {
      return [];
    }

    // Get presigned URLs for each attachment (original + variants)
    const attachmentsWithUrls = await Promise.all(
      attachments.map(async (attachment) => {
        try {
          // Build list of keys we need URLs for
          const urlRequests = [
            apiService.post(RECON.downloadUrl, { object_key: attachment.object_key }),
          ];

          // Request variant URLs if they exist
          const hasDisplay = !!attachment.display_key;
          const hasThumb = !!attachment.thumb_key;

          if (hasDisplay) {
            urlRequests.push(
              apiService.post(RECON.downloadUrl, { object_key: attachment.display_key })
            );
          }
          if (hasThumb) {
            urlRequests.push(
              apiService.post(RECON.downloadUrl, { object_key: attachment.thumb_key })
            );
          }

          const results = await Promise.all(urlRequests);

          let idx = 1;
          return {
            ...attachment,
            url: results[0].download_url,
            display_url: hasDisplay ? results[idx++]?.download_url : null,
            thumb_url: hasThumb ? results[idx++]?.download_url : null,
          };
        } catch (error) {
          console.error(
            `Failed to get download URL for attachment ${attachment.photo_id}:`,
            error
          );
          return {
            ...attachment,
            url: null,
            display_url: null,
            thumb_url: null,
          };
        }
      })
    );

    console.log(`[fetchEntityAttachments] Returning ${attachmentsWithUrls.length} attachments with URLs`);

    return attachmentsWithUrls;
  } catch (error) {
    console.error(`[fetchEntityAttachments] Failed to fetch ${entityType} attachments:`, error);
    throw error;
  }
};

/**
 * Fetch all attachments for a recon request with download URLs
 * @param {string} requestId - Recon request UUID
 * @returns {Promise<Array<Object>>} Array of attachment objects with download URLs
 */
export const fetchReconRequestAttachments = async (requestId) => {
  return fetchEntityAttachments("recon_request", requestId);
};
